import React from "react";

import { roundToWhole, useTranslation } from "utils";

import { Typography } from "components";

import { EveryLineDifference, useEveryLineTotal } from "pages/app/scenario-analysis/components/financial-breakdown/every-line/EveryLineDifference";
import type { CmYearRow } from "pages/app/scenario-analysis/utils/cmLines";

type EveryLineYearTableProps = {
  years: CmYearRow[];
  nameA: string;
  nameB: string;
  mainCropLabelLookup: Record<string, string>;
  dataTestId: string;
};

/** One row per year: when the difference arrives, with a signed bar scaled to the largest yearly difference. */
export const EveryLineYearTable = ({ years, nameA, nameB, mainCropLabelLookup, dataTestId }: EveryLineYearTableProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const formatTotal = useEveryLineTotal();
  const largestDifference = Math.max(0, ...years.map((row) => Math.abs(row.difference)));

  return (
    <div className="every-line-scroll">
      <div role="table" aria-label={t("everyLine.view.years")} className="every-line-years" data-testid={`${dataTestId}-years`}>
        <div role="row" className="every-line-year-row every-line-year-row--header">
          <span role="columnheader">{t("everyLine.header.year")}</span>
          <span role="columnheader">{t("everyLine.header.crop")}</span>
          <span role="columnheader" className="every-line-cell--value every-line-cell--name" title={nameB}>
            {nameB}
          </span>
          <span role="columnheader" className="every-line-cell--value every-line-cell--name" title={nameA}>
            {nameA}
          </span>
          <span role="columnheader" className="every-line-cell--value">
            {t("everyLine.header.difference")}
          </span>
          <span aria-hidden />
        </div>
        {years.map((row) => {
          const shownDifference = roundToWhole(row.difference);
          const barWidth = largestDifference ? `${(Math.abs(row.difference) / largestDifference) * 50}%` : "0%";
          const cropA = (row.cropIdA && mainCropLabelLookup[row.cropIdA]) || t("everyLine.noCrop");
          const cropB = (row.cropIdB && mainCropLabelLookup[row.cropIdB]) || t("everyLine.noCrop");
          // In the scenarios' column order, b then a, when their rotations differ that year.
          const cropLabel = cropA === cropB ? cropA : t("everyLine.cropPair", { cropA, cropB });
          return (
            <div key={row.year} role="row" className="every-line-year-row" data-testid={`${dataTestId}-year-${row.year}`}>
              <span role="rowheader" className="every-line-cell--muted">
                {row.year}
              </span>
              <span role="cell" className="every-line-year-crop" title={cropLabel}>
                {cropLabel}
              </span>
              <span role="cell" className="every-line-cell--value every-line-cell--muted">
                {formatTotal(row.cmB)}
              </span>
              <span role="cell" className="every-line-cell--value every-line-cell--muted">
                {formatTotal(row.cmA)}
              </span>
              <span role="cell" className="every-line-cell--value">
                <EveryLineDifference value={row.difference} />
              </span>
              <span className="every-line-bar" aria-hidden data-testid={`${dataTestId}-year-${row.year}-bar`}>
                <span className="every-line-bar-centre" />
                {shownDifference !== 0 ? (
                  <span
                    className={`every-line-bar-fill ${shownDifference > 0 ? "every-line-bar-fill--positive" : "every-line-bar-fill--negative"}`}
                    style={shownDifference > 0 ? { left: "50%", width: barWidth } : { right: "50%", width: barWidth }}
                  />
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
      <Typography variant="paragraph-s" className="every-line-note">
        {t("everyLine.yearNote")}
      </Typography>
    </div>
  );
};

export default EveryLineYearTable;
